<template>
  <div class="flex flex-col h-full" @click="closeAllPopups">

    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
      <div>
        <h2 class="text-lg font-semibold text-slate-800 dark:text-slate-100">Campanhas WhatsApp QR</h2>
        <p class="text-sm text-slate-500 dark:text-slate-400">Disparo em massa via canal não-oficial (QR Code)</p>
      </div>
      <button
        class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 transition-colors"
        @click.stop="openCreate"
      >
        <span class="text-lg leading-none">+</span> Nova campanha
      </button>
    </div>

    <!-- Sem inboxes QR -->
    <div v-if="!loading && qrInboxes.length === 0" class="flex flex-col items-center justify-center flex-1 gap-3 text-slate-400">
      <span class="text-5xl">📵</span>
      <p class="font-medium">Nenhum canal WhatsApp QR conectado</p>
      <p class="text-sm">Conecte um canal via QR Code em Configurações → Canais</p>
    </div>

    <!-- Loading -->
    <div v-else-if="loading" class="flex items-center justify-center flex-1 gap-2.5 text-slate-400 text-sm">
      <span class="w-4 h-4 border-2 border-slate-200 border-t-woot-500 rounded-full animate-spin"></span>
      Carregando campanhas...
    </div>

    <!-- Empty -->
    <div v-else-if="campaigns.length === 0" class="flex flex-col items-center justify-center flex-1 gap-3 text-slate-400 py-16">
      <span class="text-5xl">📋</span>
      <p class="font-medium">Nenhuma campanha criada</p>
      <p class="text-sm">Clique em "+ Nova campanha" para começar</p>
    </div>

    <!-- ===== TABLE ===== -->
    <div v-else class="flex-1 overflow-y-auto">
      <table class="w-full border-collapse">
        <thead class="sticky top-0 z-10">
          <tr class="bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700">
            <th class="text-left px-6 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Campanha</th>
            <th class="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider w-72">Progresso</th>
            <th class="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider w-36">Status</th>
            <th class="px-4 py-3 w-24"></th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
          <tr
            v-for="c in campaigns"
            :key="c.id"
            class="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors group"
          >
            <!-- Campanha: nome + canal -->
            <td class="px-6 py-4">
              <div class="flex items-center gap-3">
                <div
                  class="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-sm"
                  :class="isProcessing(c) ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-woot-100 dark:bg-woot-900/30'"
                >
                  {{ isProcessing(c) ? '⚙️' : '📣' }}
                </div>
                <div class="min-w-0">
                  <button
                    class="font-semibold text-slate-800 dark:text-slate-100 hover:text-woot-600 dark:hover:text-woot-400 text-sm text-left block max-w-xs truncate transition-colors"
                    @click.stop="openDetail(c)"
                  >{{ c.title }}</button>
                  <div class="text-xs text-slate-400 mt-0.5 truncate">{{ c.inbox_name }}</div>
                </div>
              </div>
            </td>

            <!-- Progresso -->
            <td class="px-4 py-4">
              <div class="flex items-center gap-2">
                <div class="flex-1 bg-slate-100 dark:bg-slate-700 rounded-full h-1.5 overflow-hidden">
                  <div
                    class="h-1.5 rounded-full transition-all duration-700"
                    :class="progressBarColor(c)"
                    :style="{ width: progressPct(c) + '%' }"
                  ></div>
                </div>
                <span class="text-xs font-semibold text-slate-500 dark:text-slate-400 flex-shrink-0 w-9 text-right">{{ progressPct(c) }}%</span>
              </div>
              <div class="flex items-center gap-3 mt-1.5 text-xs text-slate-400">
                <span>{{ sentCount(c) }}/{{ c.stats.total }} enviados</span>
                <span v-if="c.stats.failed > 0" class="text-red-500 font-medium">{{ c.stats.failed }} falhas</span>
              </div>
            </td>

            <!-- Status badge -->
            <td class="px-4 py-4">
              <span :class="statusBadgeClass(c)" class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
                <span v-if="isProcessing(c)" class="w-2.5 h-2.5 border-2 border-current border-t-transparent rounded-full animate-spin flex-shrink-0"></span>
                <span v-else-if="c.paused">⏸</span>
                <span v-else-if="isCompleted(c) && !hasFailed(c)">✓</span>
                {{ statusLabel(c) }}
              </span>
            </td>

            <!-- Actions -->
            <td class="px-4 py-4">
              <div class="flex items-center gap-1 justify-end" @click.stop>

                <!-- ⓘ info popup -->
                <div class="relative">
                  <button
                    class="w-7 h-7 flex items-center justify-center rounded-full text-slate-300 hover:text-woot-600 hover:bg-woot-50 dark:hover:bg-woot-900/30 dark:hover:text-woot-400 transition-colors text-sm font-bold leading-none opacity-0 group-hover:opacity-100"
                    title="Estatísticas detalhadas"
                    @click.stop="toggleInfo(c.id)"
                  >ⓘ</button>

                  <Transition name="bmt-popup">
                    <div
                      v-if="infoPopup === c.id"
                      class="absolute right-0 top-9 z-50 w-60 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 p-4"
                    >
                      <div class="flex items-center justify-between mb-3">
                        <span class="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Detalhes</span>
                        <button class="text-slate-300 hover:text-slate-600 text-sm leading-none" @click.stop="infoPopup = null">✕</button>
                      </div>
                      <div class="space-y-0 text-xs">
                        <div class="flex justify-between items-center py-1.5 border-b border-slate-100 dark:border-slate-700">
                          <span class="text-slate-400">Progresso</span>
                          <span class="font-bold text-slate-700 dark:text-slate-200">{{ progressPct(c) }}%</span>
                        </div>
                        <div class="flex justify-between items-center py-1.5">
                          <span class="text-slate-400">Total leads</span>
                          <span class="font-medium text-slate-700 dark:text-slate-200">{{ c.stats.total }}</span>
                        </div>
                        <div class="flex justify-between items-center py-1.5">
                          <span class="text-slate-400">Enviadas</span>
                          <span class="font-medium text-blue-600">{{ sentCount(c) }}</span>
                        </div>
                        <div class="flex justify-between items-center py-1.5">
                          <span class="text-slate-400">Recebidas</span>
                          <span class="font-medium text-indigo-600">{{ c.stats.delivered }}</span>
                        </div>
                        <div class="flex justify-between items-center py-1.5">
                          <span class="text-slate-400">Lidas</span>
                          <span class="font-medium text-woot-600">{{ c.stats.read }}</span>
                        </div>
                        <div class="flex justify-between items-center py-1.5">
                          <span class="text-slate-400">Respondidas</span>
                          <span class="font-medium text-emerald-600">{{ c.stats.replied }}</span>
                        </div>
                        <div class="flex justify-between items-center py-1.5">
                          <span class="text-slate-400">Pendentes</span>
                          <span class="font-medium text-orange-500">{{ c.stats.pending }}</span>
                        </div>
                        <div class="flex justify-between items-center py-1.5 border-t border-slate-100 dark:border-slate-700">
                          <span class="text-slate-400">Falhas</span>
                          <span class="font-medium text-red-500">{{ c.stats.failed }}</span>
                        </div>
                        <div v-if="c.trigger_rules && c.trigger_rules.triggered_at" class="flex justify-between items-center py-1.5 border-t border-slate-100 dark:border-slate-700">
                          <span class="text-slate-400">Início</span>
                          <span class="font-medium text-slate-600 dark:text-slate-300 text-right">{{ formatDateShort(c.trigger_rules.triggered_at) }}</span>
                        </div>
                        <div v-if="durationEstimate(c)" class="flex justify-between items-center py-1.5">
                          <span class="text-slate-400">Duração est.</span>
                          <span class="font-medium text-slate-600 dark:text-slate-300">{{ durationEstimate(c) }}</span>
                        </div>
                      </div>
                    </div>
                  </Transition>
                </div>

                <!-- ⋯ menu dropdown -->
                <div class="relative">
                  <button
                    class="w-7 h-7 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 dark:hover:text-slate-200 transition-colors font-bold leading-none text-base opacity-0 group-hover:opacity-100"
                    @click.stop="toggleMenu(c.id)"
                  >···</button>

                  <Transition name="bmt-popup">
                    <div
                      v-if="openMenu === c.id"
                      class="absolute right-0 top-9 z-50 w-44 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 py-1 overflow-hidden"
                    >
                      <button
                        class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors text-left"
                        @click.stop="openDetail(c)"
                      >
                        <span>👁</span> Ver detalhes
                      </button>
                      <button
                        v-if="canTrigger(c)"
                        class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors text-left"
                        :disabled="triggering === c.id"
                        @click.stop="triggerCampaign(c)"
                      >
                        <span>▶️</span> {{ triggering === c.id ? 'Disparando...' : 'Disparar' }}
                      </button>
                      <button
                        v-if="canPause(c)"
                        class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors text-left"
                        :disabled="pausing === c.id"
                        @click.stop="pauseCampaign(c)"
                      >
                        <span>⏸</span> Pausar
                      </button>
                      <button
                        v-if="c.paused"
                        class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors text-left"
                        :disabled="pausing === c.id"
                        @click.stop="resumeCampaign(c)"
                      >
                        <span>▶️</span> Retomar
                      </button>
                      <div class="border-t border-slate-100 dark:border-slate-700 my-1"></div>
                      <button
                        class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-left"
                        @click.stop="confirmDelete(c)"
                      >
                        <span>🗑️</span> Excluir
                      </button>
                    </div>
                  </Transition>
                </div>

              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ====== MODAL: CRIAR CAMPANHA ====== -->
    <div v-if="showCreate" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">

        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
          <h3 class="text-base font-semibold text-slate-800 dark:text-slate-100">Nova campanha WhatsApp QR</h3>
          <button class="text-slate-400 hover:text-slate-600 text-xl leading-none" @click="closeCreate">✕</button>
        </div>

        <div class="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          <!-- Nome + Canais -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Nome da campanha *</label>
              <input
                v-model="form.title"
                type="text"
                placeholder="Ex: Oferta consignado abril"
                class="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-300"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Canais de disparo *
                <span class="font-normal text-slate-400 ml-1">— distribuídos aleatoriamente</span>
              </label>

              <!-- Chips dos canais selecionados -->
              <div v-if="form.inbox_ids.length" class="flex flex-wrap gap-1 mb-1.5">
                <span
                  v-for="id in form.inbox_ids"
                  :key="id"
                  class="inline-flex items-center gap-1 px-2 py-0.5 bg-woot-100 dark:bg-woot-900/40 text-woot-700 dark:text-woot-300 rounded-full text-xs font-medium"
                >
                  {{ inboxNameById(id) }}
                  <button type="button" class="hover:text-woot-900 dark:hover:text-woot-100 leading-none" @click.stop="removeInboxId(id)">×</button>
                </span>
              </div>

              <!-- Dropdown trigger -->
              <div class="relative" @click.stop>
                <button
                  type="button"
                  class="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-400 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-woot-300"
                  @click="showInboxDropdown = !showInboxDropdown"
                >
                  <span>{{ form.inbox_ids.length ? 'Adicionar canal...' : 'Selecione os canais' }}</span>
                  <span class="text-slate-300 text-xs">▾</span>
                </button>
                <div
                  v-if="showInboxDropdown"
                  class="absolute z-20 left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden"
                >
                  <button
                    v-for="inbox in qrInboxes"
                    :key="inbox.inbox_id"
                    type="button"
                    class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors"
                    :class="form.inbox_ids.includes(inbox.inbox_id)
                      ? 'bg-woot-50 dark:bg-woot-900/30 text-woot-700 dark:text-woot-300'
                      : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60'"
                    @click="toggleInboxId(inbox.inbox_id)"
                  >
                    <span class="w-4 text-center text-xs">{{ form.inbox_ids.includes(inbox.inbox_id) ? '✓' : '' }}</span>
                    {{ inbox.inbox_name }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Sprint #47: Modo de disparo -->
          <div>
            <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Modo de disparo *</label>
            <div class="flex gap-2">
              <button
                type="button"
                :class="['flex-1 px-3 py-2 rounded-lg border text-sm transition-colors text-left',
                         form.mode === 'direct' ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/30 text-woot-700 dark:text-woot-300 font-medium'
                                                : 'border-slate-200 dark:border-slate-700 text-slate-500 hover:border-slate-300']"
                @click="form.mode = 'direct'"
              >
                💬 Mensagem direta
                <span class="block text-xs opacity-70 mt-0.5">Texto + imagem + botões</span>
              </button>
              <button
                type="button"
                :class="['flex-1 px-3 py-2 rounded-lg border text-sm transition-colors text-left',
                         form.mode === 'bot' ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/30 text-woot-700 dark:text-woot-300 font-medium'
                                             : 'border-slate-200 dark:border-slate-700 text-slate-500 hover:border-slate-300']"
                @click="form.mode = 'bot'"
              >
                🤖 Bot interativo
                <span class="block text-xs opacity-70 mt-0.5">Áudio + agente IA + flow</span>
              </button>
            </div>
          </div>

          <!-- Sprint #47: Bot interativo — combobox + skip -->
          <template v-if="form.mode === 'bot'">
            <div>
              <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Bot a disparar *</label>
              <select
                v-model="form.bot_id"
                class="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-300"
              >
                <option :value="null" disabled>Selecione um bot...</option>
                <option v-for="bot in botOptions" :key="bot.id" :value="bot.id">{{ bot.name }}{{ bot.active ? '' : ' — inativo' }}</option>
              </select>
              <p v-if="!botOptions.length" class="text-xs text-red-400 mt-1">Nenhum bot encontrado. Crie um bot no FlowBuilder primeiro.</p>
              <p v-else class="text-xs text-slate-400 mt-1">A campanha dispara o bot direto no nó INÍCIO (ignora keyword). Use bots com nó INÍCIO bem definido.</p>
            </div>
            <div>
              <label class="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-400 cursor-pointer">
                <input v-model="form.skip_in_progress" type="checkbox" class="mt-0.5" />
                <span>
                  <strong>Pular leads em atendimento</strong>
                  <span class="block text-slate-400 mt-0.5">Lead que já tem conversa aberta com a inbox NÃO recebe a campanha (evita conflito com atendimento humano em curso).</span>
                </span>
              </label>
            </div>
          </template>

          <!-- Mensagem (somente modo direto) -->
          <template v-if="form.mode === 'direct'">
          <div>
            <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              Mensagem *
            </label>
            <textarea
              v-model="form.message"
              rows="4"
              placeholder="Olá {{name}}, temos uma oferta especial para você!"
              class="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-300 resize-none"
            />
            <div class="mt-1.5 flex flex-wrap gap-1.5">
              <span class="text-xs text-slate-400">Variáveis do CSV:</span>
              <code
                v-for="v in ['{{name}}','{{phone}}','{{1}}','{{2}}','{{3}}']"
                :key="v"
                class="text-xs bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-1.5 py-0.5 rounded cursor-pointer hover:bg-woot-50 hover:border-woot-300 transition-colors font-mono text-slate-600 dark:text-slate-300"
                :title="varHint(v)"
                @click="insertVar(v)"
              >{{ v }}</code>
            </div>
            <p class="text-xs text-slate-400 mt-1">Formatação WhatsApp: *negrito*, _itálico_. {{1}} = 1ª coluna CSV, {{2}} = 2ª, etc.</p>
          </div>

          <!-- Imagem -->
          <div>
            <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              Imagem <span class="font-normal text-slate-400">— opcional, enviada antes do texto</span>
            </label>

            <!-- Preview após upload -->
            <div v-if="form.image_url" class="flex items-center gap-3 p-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800">
              <img :src="form.image_url" class="w-14 h-14 object-cover rounded-lg flex-shrink-0" />
              <div class="flex-1 min-w-0">
                <p class="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">{{ form.imageFileName || 'Imagem anexada' }}</p>
                <p class="text-xs text-emerald-500 mt-0.5">Pronta para envio</p>
              </div>
              <button class="text-xs text-red-400 hover:text-red-600 flex-shrink-0 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" @click="removeImage">Remover</button>
            </div>

            <!-- Botão de upload -->
            <div v-else>
              <input ref="imageInput" type="file" accept="image/jpeg,image/png,image/gif,image/webp" class="hidden" @change="onImageChange" />
              <button
                class="w-full border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg px-3 py-3 text-sm transition-colors flex items-center justify-center gap-2"
                :class="imageUploading ? 'opacity-50 cursor-not-allowed text-slate-400' : 'text-slate-400 hover:border-woot-300 hover:text-woot-500 cursor-pointer'"
                :disabled="imageUploading"
                @click="!imageUploading && $refs.imageInput.click()"
              >
                <span v-if="imageUploading" class="animate-spin">⏳</span>
                <span v-else>📎</span>
                {{ imageUploading ? 'Enviando imagem...' : 'Anexar imagem (JPG, PNG, GIF)' }}
              </button>
              <p v-if="imageError" class="text-xs text-red-400 mt-1">{{ imageError }}</p>
            </div>
          </div>

          <!-- Botões interativos -->
          <div>
            <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              Botões de resposta <span class="font-normal text-slate-400">— opcional, máx. 3</span>
            </label>
            <p class="text-xs text-slate-400 mb-2">O cliente toca no botão para responder. Deixe em branco para não usar.</p>
            <div class="flex flex-col gap-2">
              <div
                v-for="(_, idx) in form.buttons"
                :key="idx"
                class="flex items-center gap-2"
              >
                <span class="text-xs text-slate-400 w-4 flex-shrink-0">{{ idx + 1 }}</span>
                <input
                  v-model="form.buttons[idx]"
                  type="text"
                  :placeholder="`Botão ${idx + 1} (ex: Sim, Quero saber mais...)`"
                  maxlength="20"
                  class="flex-1 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-300"
                />
                <span class="text-xs text-slate-300 flex-shrink-0 w-10 text-right">{{ (form.buttons[idx] || '').length }}/20</span>
              </div>
            </div>
          </div>

          </template>

          <!-- Velocidade -->
          <div>
            <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Velocidade de envio</label>
            <div class="grid grid-cols-4 gap-2 mb-3">
              <button
                v-for="preset in speedPresets"
                :key="preset.key"
                :class="[
                  'flex flex-col items-center gap-1 p-3 rounded-xl border-2 text-xs transition-all',
                  form.speed === preset.key
                    ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/30 text-woot-700 dark:text-woot-300'
                    : 'border-slate-200 dark:border-slate-700 text-slate-500 hover:border-woot-300 dark:hover:border-woot-700'
                ]"
                @click="selectSpeed(preset)"
              >
                <span class="text-xl">{{ preset.icon }}</span>
                <span class="font-semibold">{{ preset.label }}</span>
                <span v-if="preset.key !== 'custom'" class="text-center leading-tight whitespace-pre-line" style="font-size:10px">{{ preset.description }}</span>
              </button>
            </div>

            <!-- Personalizado -->
            <div v-if="form.speed === 'custom'" class="grid grid-cols-3 gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
              <div>
                <label class="block text-xs text-slate-500 mb-1">Intervalo entre msgs (seg)</label>
                <input v-model.number="form.send_interval" type="number" min="1" class="w-full border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100" />
              </div>
              <div>
                <label class="block text-xs text-slate-500 mb-1">Tamanho do lote (msgs)</label>
                <input v-model.number="form.batch_size" type="number" min="0" class="w-full border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100" />
              </div>
              <div>
                <label class="block text-xs text-slate-500 mb-1">Pausa entre lotes (seg)</label>
                <input v-model.number="form.batch_interval" type="number" min="0" class="w-full border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100" />
              </div>
            </div>

            <p v-if="durationEstimateForm" class="text-xs text-slate-500 mt-2">⏱ Duração estimada: {{ durationEstimateForm }}</p>
          </div>

          <!-- CSV Upload -->
          <div>
            <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              Importar contatos *
            </label>
            <p class="text-xs text-slate-400 mb-2">CSV com coluna "telefone" — opcional: "nome". Todas as colunas ficam disponíveis como {{1}}, {{2}}, {{3}}...</p>
            <div
              class="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors"
              :class="form.csvFile
                ? 'border-woot-400 bg-woot-50 dark:bg-woot-900/20'
                : 'border-slate-200 dark:border-slate-700 hover:border-woot-300'"
              @click="$refs.csvInput.click()"
              @dragover.prevent
              @drop.prevent="onDrop"
            >
              <input ref="csvInput" type="file" accept=".csv,.txt" class="hidden" @change="onFileChange" />
              <div v-if="!form.csvFile">
                <p class="text-3xl mb-2">📂</p>
                <p class="text-sm text-slate-500">Arraste o CSV aqui ou <span class="text-woot-600 font-medium">clique para selecionar</span></p>
                <p class="text-xs text-slate-400 mt-1">Formato: telefone;nome ou telefone,nome</p>
              </div>
              <div v-else class="text-sm">
                <p class="text-2xl mb-1">✅</p>
                <p class="font-medium text-slate-700 dark:text-slate-200">{{ form.csvFile.name }}</p>
                <p class="text-slate-500 text-xs mt-0.5">{{ form.csvPreviewCount != null ? form.csvPreviewCount + ' contatos detectados' : 'Arquivo selecionado' }}</p>
                <button class="text-xs text-red-400 hover:text-red-600 mt-2" @click.stop="clearCsv">Remover arquivo</button>
              </div>
            </div>
          </div>

        </div>

        <!-- Footer -->
        <div class="flex items-center justify-between px-6 py-4 border-t border-slate-100 dark:border-slate-800 flex-shrink-0">
          <p v-if="createError" class="text-sm text-red-500">{{ createError }}</p>
          <!-- Sprint #47.1: hint do que falta preencher quando o botao esta desabilitado -->
          <p v-else-if="formValidationIssues.length && !creating" class="text-xs text-amber-600 dark:text-amber-400">
            Falta preencher: {{ formValidationIssues.join(', ') }}
          </p>
          <div v-else />
          <div class="flex gap-3">
            <button
              class="px-4 py-2 text-sm border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              @click="closeCreate"
            >
              Cancelar
            </button>
            <button
              class="px-5 py-2 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 transition-colors disabled:opacity-50"
              :disabled="!canSubmitCampaign || creating"
              @click="createCampaign"
            >
              {{ creating ? 'Criando e disparando...' : form.csvFile ? 'Criar e disparar' : 'Criar campanha' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ====== MODAL: CONFIRMAR EXCLUSÃO ====== -->
    <div v-if="deleteTarget" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-6 w-96 text-center">
        <p class="text-4xl mb-3">🗑️</p>
        <h3 class="text-base font-semibold text-slate-800 dark:text-slate-100 mb-2">Excluir campanha?</h3>
        <p class="text-sm text-slate-500 mb-5">
          "<strong>{{ deleteTarget.title }}</strong>" e todos os seus contatos serão removidos permanentemente.
        </p>
        <div class="flex gap-3 justify-center">
          <button
            class="px-4 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-400"
            @click="deleteTarget = null"
          >
            Cancelar
          </button>
          <button
            class="px-4 py-2 text-sm font-medium bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
            :disabled="deleting"
            @click="deleteCampaign"
          >
            {{ deleting ? 'Excluindo...' : 'Sim, excluir' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ====== MODAL: DETALHES ====== -->
    <BmtQrCampaignDetail
      v-if="selectedCampaign"
      :campaign="selectedCampaign"
      @close="selectedCampaign = null"
      @refresh="loadCampaigns"
    />

  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { BmtCampaignsApi, BmtEvolutionInboxesApi } from '../../../../api/bmtCampaigns';
import BotsAPI from '../../../../api/bots';
import BmtQrCampaignDetail from './BmtQrCampaign/BmtQrCampaignDetail.vue';

const SPEED_PRESETS = [
  { key: 'slow',     label: 'Lento',        icon: '🐢', description: '1 msg/60s\nLote 30, pausa 30min', send_interval: 60,  batch_size: 30,  batch_interval: 1800 },
  { key: 'moderate', label: 'Moderado',     icon: '🚚', description: '1 msg/20s\nLote 100, pausa 15min', send_interval: 20, batch_size: 100, batch_interval: 900 },
  { key: 'fast',     label: 'Rápido',       icon: '⚡', description: '1 msg/8s\nLote 200, pausa 10min',  send_interval: 8,  batch_size: 200, batch_interval: 600 },
  { key: 'custom',   label: 'Personalizado', icon: '⚙️', description: '',                               send_interval: 20, batch_size: 100, batch_interval: 900 },
];

export default {
  name: 'BmtQrCampaignsPage',
  components: { BmtQrCampaignDetail },

  setup() {
    const campaigns        = ref([]);
    const bots             = ref([]);
    // Sprint #47.6.6: lista TODOS os bots (campanha-bot ideal = bot inativo +
    // auto-dispatch OFF; lock pos-campanha #47.6.5 garante seguranca).
    const botOptions       = computed(() => bots.value);
    const qrInboxes        = ref([]);
    const loading          = ref(true);
    const showCreate       = ref(false);
    const creating         = ref(false);
    const createError      = ref('');
    const selectedCampaign = ref(null);
    const deleteTarget     = ref(null);
    const deleting         = ref(false);
    const triggering       = ref(null);
    const pausing          = ref(null);
    const infoPopup        = ref(null);
    const openMenu         = ref(null);


    let refreshTimer = null;

    const showInboxDropdown = ref(false);

    const defaultForm = () => ({
      title: '', inbox_ids: [], message: '', image_url: '', imageFileName: '',
      speed: 'moderate', send_interval: 20, batch_size: 100, batch_interval: 900,
      csvFile: null, csvPreviewCount: null,
      buttons: ['', '', ''],
      // Sprint #47: modo de disparo (direct = msg estatica | bot = flow interativo)
      mode: 'direct', bot_id: null, skip_in_progress: false,
    });

    const imageUploading = ref(false);
    const imageError     = ref('');

    const form         = ref(defaultForm());
    const speedPresets = SPEED_PRESETS;

    // ---- status helpers ----
    const sentCount   = c => (c.stats.sent || 0) + (c.stats.delivered || 0) + (c.stats.read || 0) + (c.stats.replied || 0);
    const progressPct = c => c.stats.total > 0 ? Math.round((sentCount(c) / c.stats.total) * 100) : 0;
    const isProcessing = c => !c.paused && (c.stats.pending || 0) > 0 && (c.stats.total || 0) > 0;
    const isCompleted  = c => (c.stats.total || 0) > 0 && (c.stats.pending || 0) === 0;
    const hasFailed    = c => isCompleted(c) && (c.stats.failed || 0) > 0;
    // Disparar só aparece quando há pendentes E não está ativamente processando
    // (evita confusão quando campanha já está rodando no Sidekiq)
    const canTrigger   = c => !c.paused && !isProcessing(c) && (c.stats.pending || 0) > 0;
    const canPause     = c => !c.paused && (c.stats.pending || 0) > 0;

    const statusLabel = c => {
      if (c.paused)       return 'Pausada';
      if (isProcessing(c)) return 'Processando';
      if (hasFailed(c))   return 'Com falhas';
      if (isCompleted(c)) return 'Concluída';
      if (c.stats.total > 0) return 'Ativa';
      return 'Ativa';
    };

    const statusBadgeClass = c => {
      if (c.paused)        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      if (isProcessing(c)) return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
      if (hasFailed(c))    return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      if (isCompleted(c))  return 'bg-woot-100 text-woot-700 dark:bg-woot-900/30 dark:text-woot-400';
      return 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300';
    };

    const progressBarColor = c => {
      if (c.paused)        return 'bg-yellow-400';
      if (isProcessing(c)) return 'bg-purple-500';
      if (hasFailed(c))    return 'bg-red-400';
      if (isCompleted(c))  return 'bg-woot-500';
      return 'bg-woot-500';
    };

    // ---- duration estimate ----
    const durationEstimate = c => {
      const total    = c.stats && c.stats.total;
      const rules    = c.trigger_rules || {};
      if (!total) return null;
      const interval = rules.send_interval  || 20;
      const bSize    = rules.batch_size     || 0;
      const bPause   = rules.batch_interval || 0;
      let sec = bSize > 0 && bPause > 0
        ? ((Math.ceil(total / bSize) - 1) * bPause) + ((total - 1) * interval)
        : (total - 1) * interval;
      if (sec < 60)   return `~${sec}s`;
      if (sec < 3600) return `~${Math.round(sec / 60)} min`;
      const h = Math.floor(sec / 3600);
      const m = Math.round((sec % 3600) / 60);
      return `~${h}h${m > 0 ? ` ${m}min` : ''}`;
    };

    // Sprint #47.1: validacao visual com hint do que esta faltando (por modo)
    const formValidationIssues = computed(() => {
      const issues = [];
      if (!form.value.title || !form.value.title.trim()) issues.push('nome');
      if (!form.value.inbox_ids || !form.value.inbox_ids.length) issues.push('canal');
      if (form.value.mode === 'direct' && (!form.value.message || !form.value.message.trim())) issues.push('mensagem');
      if (form.value.mode === 'bot' && !form.value.bot_id) issues.push('bot');
      if (!form.value.csvFile) issues.push('CSV de contatos');
      return issues;
    });
    const canSubmitCampaign = computed(() => formValidationIssues.value.length === 0);

    const durationEstimateForm = computed(() => {
      const total = form.value.csvPreviewCount;
      if (!total || total <= 0) return null;
      const interval = form.value.send_interval   || 20;
      const bSize    = form.value.batch_size       || 0;
      const bPause   = form.value.batch_interval   || 0;
      let sec = bSize > 0 && bPause > 0
        ? ((Math.ceil(total / bSize) - 1) * bPause) + ((total - 1) * interval)
        : (total - 1) * interval;
      if (sec < 60)   return `~${sec} segundos`;
      if (sec < 3600) return `~${Math.round(sec / 60)} minutos`;
      const h = Math.floor(sec / 3600);
      const m = Math.round((sec % 3600) / 60);
      return `~${h}h${m > 0 ? ` ${m}min` : ''}`;
    });

    // ---- popups ----
    function toggleInfo(id) { infoPopup.value = infoPopup.value === id ? null : id; openMenu.value = null; }
    function toggleMenu(id) { openMenu.value  = openMenu.value  === id ? null : id; infoPopup.value = null; }
    function closeAllPopups() { infoPopup.value = null; openMenu.value = null; showInboxDropdown.value = false; }

    // ---- data loading ----
    async function loadInboxes() {
      try {
        const { data } = await BmtEvolutionInboxesApi.get();
        qrInboxes.value = Array.isArray(data) ? data : [];
      } catch { qrInboxes.value = []; }
    }

    async function loadCampaigns() {
      try {
        const { data } = await BmtCampaignsApi.get();
        campaigns.value = Array.isArray(data) ? data : [];
      } catch { campaigns.value = []; }
    }

    onMounted(async () => {
      // Sprint #47: carrega bots da conta pro combobox do modo Bot interativo.
      BotsAPI.get().then(({ data }) => { bots.value = data || []; }).catch(() => {});
      loading.value = true;
      await Promise.all([loadInboxes(), loadCampaigns()]);
      loading.value = false;
      // Auto-refresh enquanto houver campanhas processando
      refreshTimer = setInterval(() => {
        if (campaigns.value.some(c => isProcessing(c))) loadCampaigns();
      }, 10000);
    });

    onUnmounted(() => {
      if (refreshTimer) clearInterval(refreshTimer);
    });

    // ---- create modal ----
    function openCreate()  { form.value = defaultForm(); createError.value = ''; showCreate.value = true; }
    function closeCreate() { showCreate.value = false; }

    function selectSpeed(preset) {
      form.value.speed          = preset.key;
      form.value.send_interval  = preset.send_interval;
      form.value.batch_size     = preset.batch_size;
      form.value.batch_interval = preset.batch_interval;
    }

    function onFileChange(e) { const f = e.target.files[0]; if (f) setFile(f); }
    function onDrop(e)       { const f = e.dataTransfer.files[0]; if (f) setFile(f); }

    function setFile(file) {
      form.value.csvFile = file;
      const reader = new FileReader();
      reader.onload = ev => {
        const lines = ev.target.result.split('\n').filter(l => l.trim()).length;
        form.value.csvPreviewCount = Math.max(0, lines - 1);
      };
      reader.readAsText(file);
    }

    function clearCsv() { form.value.csvFile = null; form.value.csvPreviewCount = null; }

    async function onImageChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      imageUploading.value = true;
      imageError.value = '';
      try {
        const { data } = await BmtCampaignsApi.uploadImage(file);
        form.value.image_url    = data.url;
        form.value.imageFileName = file.name;
      } catch (err) {
        imageError.value = err?.response?.data?.error || 'Erro ao enviar imagem.';
      } finally {
        imageUploading.value = false;
        if (e.target) e.target.value = '';
      }
    }

    function removeImage() {
      form.value.image_url     = '';
      form.value.imageFileName = '';
      imageError.value         = '';
    }

    // ---- multi-canal helpers ----
    function inboxNameById(id) {
      const inbox = qrInboxes.value.find(i => i.inbox_id === id);
      return inbox ? inbox.inbox_name : id;
    }
    function toggleInboxId(id) {
      const idx = form.value.inbox_ids.indexOf(id);
      if (idx === -1) form.value.inbox_ids.push(id);
      else form.value.inbox_ids.splice(idx, 1);
    }
    function removeInboxId(id) {
      form.value.inbox_ids = form.value.inbox_ids.filter(i => i !== id);
    }

    function varHint(v) {
      const hints = { '{{name}}': 'Nome do contato', '{{phone}}': 'Telefone', '{{1}}': '1ª coluna do CSV', '{{2}}': '2ª coluna do CSV', '{{3}}': '3ª coluna do CSV' };
      return hints[v] || v;
    }

    function insertVar(v) {
      form.value.message = (form.value.message || '') + v;
    }

    async function createCampaign() {
      if (!form.value.title || !form.value.inbox_ids.length) return;
      if (form.value.mode === 'direct' && !form.value.message) return;
      if (form.value.mode === 'bot' && !form.value.bot_id) return;
      createError.value = '';
      creating.value    = true;
      showInboxDropdown.value = false;
      try {
        const validButtons = form.value.buttons.map(b => b.trim()).filter(b => b.length > 0);
        const { data: campaign } = await BmtCampaignsApi.create({
          campaign: {
            title:     form.value.title,
            message:   form.value.mode === 'bot' ? '[Bot interativo]' : form.value.message,
            inbox_id:  form.value.inbox_ids[0],
            inbox_ids: form.value.inbox_ids,
            buttons:   form.value.mode === 'direct' && validButtons.length > 0 ? validButtons : null,
            trigger_rules: {
              send_interval:  form.value.send_interval,
              batch_size:     form.value.batch_size,
              batch_interval: form.value.batch_interval,
              image_url:      form.value.mode === 'direct' ? (form.value.image_url || null) : null,
              paused:         false,
              // Sprint #47: modo bot interativo
              mode:             form.value.mode,
              bot_id:           form.value.mode === 'bot' ? form.value.bot_id : null,
              skip_in_progress: form.value.mode === 'bot' ? form.value.skip_in_progress : false,
            },
          },
        });
        if (form.value.csvFile) {
          await BmtCampaignsApi.uploadContacts(campaign.id, form.value.csvFile);
          // Auto-disparar após importar contatos
          await BmtCampaignsApi.trigger(campaign.id);
        }
        closeCreate();
        await loadCampaigns();
      } catch (err) {
        createError.value = err?.response?.data?.error || 'Erro ao criar campanha.';
      } finally {
        creating.value = false;
      }
    }

    // ---- campaign actions ----
    function openDetail(c) { selectedCampaign.value = c; closeAllPopups(); }

    async function triggerCampaign(c) {
      closeAllPopups();
      triggering.value = c.id;
      try {
        await BmtCampaignsApi.trigger(c.id);
        await loadCampaigns();
      } catch (err) {
        alert(err?.response?.data?.error || 'Erro ao disparar campanha');
      } finally { triggering.value = null; }
    }

    async function pauseCampaign(c) {
      closeAllPopups();
      pausing.value = c.id;
      try { await BmtCampaignsApi.pause(c.id); await loadCampaigns(); }
      catch { alert('Erro ao pausar campanha'); }
      finally { pausing.value = null; }
    }

    async function resumeCampaign(c) {
      closeAllPopups();
      pausing.value = c.id;
      try { await BmtCampaignsApi.resume(c.id); await loadCampaigns(); }
      catch { alert('Erro ao retomar campanha'); }
      finally { pausing.value = null; }
    }

    function confirmDelete(c) { deleteTarget.value = c; closeAllPopups(); }

    async function deleteCampaign() {
      if (!deleteTarget.value) return;
      deleting.value = true;
      try {
        await BmtCampaignsApi.destroy(deleteTarget.value.id);
        deleteTarget.value = null;
        await loadCampaigns();
      } catch { alert('Erro ao excluir campanha'); }
      finally { deleting.value = false; }
    }

    function formatDateShort(iso) {
      if (!iso) return '—';
      return new Date(iso).toLocaleString('pt-BR', {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
      });
    }

    return {
      campaigns, qrInboxes, loading,
      showCreate, creating, createError, form, speedPresets, durationEstimateForm,
      bots, botOptions,
      selectedCampaign, deleteTarget, deleting, triggering, pausing,
      infoPopup, openMenu,
      sentCount, progressPct, isProcessing, isCompleted, hasFailed,
      statusLabel, statusBadgeClass, progressBarColor,
      canTrigger, canPause, durationEstimate,
      toggleInfo, toggleMenu, closeAllPopups,
      openCreate, closeCreate, selectSpeed,
      onFileChange, onDrop, clearCsv, varHint, insertVar,
      imageUploading, imageError, onImageChange, removeImage,
      createCampaign, openDetail, triggerCampaign, pauseCampaign, resumeCampaign,
      formValidationIssues, canSubmitCampaign,
      confirmDelete, deleteCampaign, loadCampaigns, formatDateShort,
      showInboxDropdown, inboxNameById, toggleInboxId, removeInboxId,
    };
  },
};
</script>

<style scoped>
.bmt-popup-enter-active,
.bmt-popup-leave-active {
  transition: opacity 0.12s ease, transform 0.12s ease;
}
.bmt-popup-enter-from,
.bmt-popup-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.97);
}
</style>
